import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { ArrowDownToLine, ArrowLeftRight, ArrowUpFromLine, Search, SquareArrowOutUpRight, TrendingUp } from 'lucide-react'
import { useTRPC } from '@/lib/trpc'
import type { Holding, MeWallet, RouterOutputs } from '@/lib/api-types'
import { buildActivityRows, pricesFromHoldings, type ActivityKind, type ActivityRow } from '@/lib/activity'
import { feeReceiptText, formatFeeShort, formatRawTokenAmount, formatTokenAmount, formatTokenAmountShort, formatUsdShort, plural, shortAddress } from '@/lib/format'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { Segmented } from './segmented'
import { Spinner } from './spinner'
import { Age } from './age'
import { TokenIcon } from './holdings-table'
import { PlatformLogo, type Platform } from './custody-logo'
import { PlatformMark, platformLabel } from './platform-chip'
import { useCustody, CUSTODY_LABELS, useKrakenAvailability } from '@/lib/pool'
import { useRefreshActivity, type ActivityKind as FeedKind } from '@/lib/activity-feed'
import { PIN, useScrolledSideways } from './pinned-column'

type Kind = ActivityKind
/** 'strategy': the strategies' orders, which are swaps too; the Swaps tab then keeps only the user's own (John, Nov 1 2026) */
type Tab = 'all' | Kind | 'strategy'

const STATUS_LABEL: Record<string, string> = {
  completed: 'Completed',
  confirmed: 'Completed',
  pending: 'Pending',
  pendingUserConfirm: 'Quoted',
  transactionSent: 'Sent',
  errored: 'Failed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/** rows revealed per step */
const PAGE = 10
/**
 * One axis per scroller (John, Nov 2 2026: on his phone a thumb drag on the card went diagonal, it scrolled both ways at
 * once). Phones and touch screens: the card grows with its rows, the page scrolls down, the table only moves sideways in
 * its wrapper. Mouse screens from the tablet width up keep the fixed-height card that scrolls inside.
 */
const CARD_SCROLL = 'sm:pointer-fine:max-h-[30rem]'

/** phones show All and then each kind's icon, the one its rows wear in the Type column; the words return from the tablet width up (John, Nov 1 2026) */
function tabLabel(text: string, Icon: typeof ArrowLeftRight) {
  return (
    <>
      {/* svgs are block-level (Tailwind's reset), so the icon needs its own centering; the words center as text does */}
      <Icon className="mx-auto size-4 sm:hidden" aria-hidden="true" />
      <span className="hidden sm:inline">{text}</span>
    </>
  )
}

const TABS: { value: Tab; label: React.ReactNode; title?: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'swap', label: tabLabel('Swaps', ArrowLeftRight), title: 'Swaps' },
  { value: 'strategy', label: tabLabel('Strategies', TrendingUp), title: 'Strategies' },
  { value: 'deposit', label: tabLabel('Deposits', ArrowDownToLine), title: 'Deposits' },
  { value: 'send', label: tabLabel('Withdraws', ArrowUpFromLine), title: 'Withdraws' },
]

/** what a tab says when it has nothing to list */
const EMPTY: Record<Tab, string> = {
  all: 'No transactions yet.',
  swap: 'No swaps yet.',
  strategy: 'No strategy orders yet.',
  deposit: 'No deposits yet.',
  send: 'No withdrawals yet.',
}

/** a cursor-paged list: its newest rows first, the older ones a page at a time */
type Paged = { data?: { pages: { items: { createdAt?: string }[] }[] }; hasNextPage: boolean; isPending: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown }
type Source = { isPending: boolean; isError?: boolean }
const items = <T,>(q: { data?: { pages: { items: T[] }[] } }): T[] => q.data?.pages.flatMap((p) => p.items) ?? []

type Flow = RouterOutputs['progress']['list']['flows'][number]
type FlowToken = Extract<Flow['title'][number], { type: 'token' }>
const CORE_SYMBOLS = new Set(['SOL', 'USDC', 'USDT'])
const CORE_BY_SYMBOL: Record<string, string> = { SOL: SOL_MINT, USDC: USDC_MINT, USDT: USDT_MINT }
const amountOf = (t: FlowToken): number | undefined => (t.amountRaw && t.decimals !== null ? Number(t.amountRaw) / 10 ** t.decimals : (t.amountUi ?? undefined))
const amountText = (t: FlowToken) => (t.amountRaw && t.decimals !== null ? `${formatRawTokenAmount(t.amountRaw, t.decimals, t.symbol)} ${t.symbol}` : t.amountUi !== null ? `${formatTokenAmount(t.amountUi, t.symbol)} ${t.symbol}` : undefined)

/**
 * Whatever is in progress, as a Pending row (John, Nov 3 2026: a DCA order running and no row for it; "make the table
 * automatically show in progress things"): one row per flow of the bottom bar's feed, so an order shows while it
 * fills, a sale while it funds, a deposit while the exchange confirms it, whether or not a record of its own exists
 * yet. Its age counts from when the flow started, or from when it was first seen here.
 */
function pendingRows(flows: Flow[], prices: Map<string, number>, firstSeen: Map<string, string>): ActivityRow[] {
  return flows.map((flow) => {
    const tokens = flow.title.filter((p): p is FlowToken => p.type === 'token')
    const firstText = flow.title.find((p) => p.type === 'text')
    const sol = flow.title.find((p) => p.type === 'amount')
    const paidToken = tokens[0]
    // the traded token names the row: a swap's non-core side, else the first token on the chip
    const named = flow.kind === 'swap' && paidToken && CORE_SYMBOLS.has(paidToken.symbol) ? (tokens[1] ?? paidToken) : paidToken
    const symbol = (named?.symbol ?? '').toUpperCase()
    const kind: ActivityKind = flow.kind === 'deposit' ? 'deposit' : flow.kind === 'withdraw' ? 'send' : 'swap'
    const type =
      flow.kind === 'deposit' ? 'Deposit'
        : flow.kind === 'withdraw' ? (firstText?.type === 'text' ? firstText.text : 'Withdraw')
          : flow.kind === 'sell' ? 'Sell'
            : flow.kind === 'buy' ? 'Buy'
              : flow.kind === 'strategy' ? (firstText?.type === 'text' && firstText.text.endsWith('Sell') ? 'Sell' : 'Buy')
                : paidToken && CORE_SYMBOLS.has(paidToken.symbol) ? 'Buy' : 'Sell'
    // what leaves: a buy's SOL, else the first token with an amount
    const paid = sol?.type === 'amount' ? `${formatRawTokenAmount(sol.amountRaw, sol.decimals, sol.symbol)} ${sol.symbol}` : paidToken ? amountText(paidToken) : undefined
    const amount = paidToken ? amountOf(paidToken) : undefined
    const price = paidToken ? prices.get(paidToken.address ?? CORE_BY_SYMBOL[paidToken.symbol] ?? '') : undefined
    let startedAt = flow.startedAt ?? firstSeen.get(flow.id)
    if (!startedAt) {
      startedAt = new Date().toISOString()
      firstSeen.set(flow.id, startedAt)
    }
    return {
      id: `flow-${flow.id}`,
      kind,
      type,
      symbol,
      imageUrl: named ? tokenLogo(named.address ?? CORE_BY_SYMBOL[named.symbol], named.imageUrl ?? undefined) : undefined,
      paid: kind === 'deposit' ? undefined : paid,
      received: kind === 'deposit' ? (paid ?? '…') : '…',
      valueUsd: amount !== undefined && price !== undefined ? amount * price : undefined,
      fees: null,
      status: 'pending',
      createdAt: startedAt,
      strategyId: flow.target.kind === 'strategy' ? flow.target.id : undefined,
      platform: flow.platform as Platform,
      searchText: `${type} ${symbol} pending ${flow.stage}`,
    }
  })
}

/** a row's status once nothing about it is still moving */
const SETTLED = new Set(['completed', 'confirmed', 'errored', 'failed', 'cancelled'])
/** how long a flow that left the feed waits on the table for its record's row before it is let go */
const LINGER_MS = 30_000
/** how long before a flow's stamped start its record may have been written and still be its own */
const RECORD_LEAD_MS = 2 * 60_000
/** the lists a flow's record lands in, by what the flow was for: read again the moment the flow leaves the feed */
const RECORD_KINDS: Record<Flow['target']['kind'], FeedKind[]> = {
  deposit: ['deposit', 'privateDeposit'],
  withdrawal: ['withdrawal'],
  privateWithdrawal: ['privateWithdrawal'],
  transfer: ['transfer'],
  position: ['swap', 'position'],
  swap: ['swap'],
  strategy: ['swap', 'strategy'],
}

/**
 * The record row a flow becomes, when it is on the table: a deposit's, withdrawal's or transfer's own row by id, an
 * order's or a position's by the swap made since the flow began. The handover (John, Nov 3 2026: order rows vanished
 * for five to ten seconds between the Pending row leaving and the completed row arriving): a live flow's row yields
 * to its record once that is settled, and a flow that left the feed keeps its row until the record is here.
 */
function recordOf(flow: Flow, records: ActivityRow[]): ActivityRow | undefined {
  const { kind, id } = flow.target
  // the swap is written a moment before the flow's own start is stamped (a sale's `sellingAt`, an order's `lastOrderAt`),
  // so a record from shortly before the flow began is still its own; the newest record wins (records come newest first)
  const since = flow.startedAt ? Date.parse(flow.startedAt) - RECORD_LEAD_MS : Number.NEGATIVE_INFINITY
  const madeSince = (r: ActivityRow) => (r.createdAt ? Date.parse(r.createdAt) : Number.NEGATIVE_INFINITY) >= since
  switch (kind) {
    case 'deposit':
      return records.find((r) => r.id === `d-${id}` || r.id === `vd-${id}` || r.id.startsWith(`pd-${id}-`))
    case 'withdrawal':
      return records.find((r) => r.id === `vw-${id}`)
    case 'privateWithdrawal':
      return records.find((r) => r.id === `pw-${id}`)
    case 'transfer':
      return records.find((r) => r.id === `t-${id}`)
    case 'swap':
      return records.find((r) => r.id === `s-${id}`)
    case 'position':
      return records.find((r) => r.kind === 'swap' && r.positionId === id && madeSince(r))
    case 'strategy':
      return records.find((r) => r.kind === 'swap' && r.strategyId === id && madeSince(r))
  }
}

/** a record's own pending row while its flow is on the table: the flow row says it once */
function coveredByFlow(row: ActivityRow, flows: Flow[]): boolean {
  if (row.status !== 'pending') return false
  return flows.some(
    (flow) =>
      row.id.includes(flow.target.id) ||
      (flow.target.kind === 'strategy' && row.strategyId === flow.target.id) ||
      // a position wallet's swap belongs to the buy or sale running in it
      (flow.target.kind === 'position' && row.kind === 'swap' && row.isPrivate === true && !row.strategyId),
  )
}

/**
 * Every deposit, withdrawal and swap of the wallet in one table, like Sonar's
 * dashboard history: tabs per kind, a search box, newest first, pages on
 * demand. Every platform's records show at all times (John, Oct 31 2026), the
 * Platform column naming where each happened. The All tab shows every column; a kind's own tab drops the ones
 * that never apply to it (no Out on deposits, no From / To on swaps).
 */
export function TransactionsTable({ wallet, holdings = [] }: { wallet: MeWallet; holdings?: Holding[] }) {
  const trpc = useTRPC()
  const [tab, setTab] = useState<Tab>('all')
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState(PAGE)
  const scroller = useRef<HTMLDivElement>(null)
  const sentinel = useRef<HTMLDivElement>(null)
  const sideways = useScrolledSideways()
  const { exchange, custody } = useCustody()
  const custodyLabel = CUSTODY_LABELS[custody]
  const kraken = useKrakenAvailability()
  /*
   * Every tab's lists load from the start, so a switch shows a finished list at once (John, Nov 3 2026: on the Swaps
   * tab Kraken's sales showed first, then the exchange sales slid in between them page by page). Only the open tab's
   * lists keep polling; the activity feed reads them all again the moment a record changes.
   */
  const live = (active: boolean, interval = 15_000): { refetchInterval: number | false; refetchOnWindowFocus: boolean } => ({ refetchInterval: active ? interval : false, refetchOnWindowFocus: true })
  const paged = (active: boolean) => ({ ...live(active), initialPageParam: null as string | null })
  const next = { getNextPageParam: (last: { nextCursor?: string | null }) => last.nextCursor ?? undefined }
  const deposits = useInfiniteQuery({ ...trpc.deposit.list.infiniteQueryOptions({ walletIndex: wallet.index }, next), ...paged(tab === 'all' || tab === 'deposit') })
  const transfers = useInfiniteQuery({ ...trpc.transfer.list.infiniteQueryOptions({ walletIndex: wallet.index }, next), ...paged(tab === 'all' || tab === 'send') })
  // the swaps three ways: everything for All, the user's own trades for Swaps, the strategies' orders for Strategies, so
  // each tab's pages are full of its own rows (the orders outnumber the hand trades many times over)
  const swapsAll = useInfiniteQuery({ ...trpc.swap.list.infiniteQueryOptions({ walletIndex: wallet.index }, next), ...paged(tab === 'all') })
  const swapsOwn = useInfiniteQuery({ ...trpc.swap.list.infiniteQueryOptions({ walletIndex: wallet.index, strategy: 'none' }, next), ...paged(tab === 'swap') })
  const swapsOrders = useInfiniteQuery({ ...trpc.swap.list.infiniteQueryOptions({ walletIndex: wallet.index, strategy: 'only' }, next), ...paged(tab === 'strategy') })
  const privateWithdrawals = useQuery({ ...trpc.privateWithdrawal.list.queryOptions(), ...live(tab === 'all' || tab === 'send') })
  const privateDeposits = useQuery({ ...trpc.privateDeposit.list.queryOptions(), ...live(tab === 'all' || tab === 'deposit') })
  const positions = useQuery({ ...trpc.position.list.queryOptions(), ...live(tab === 'all' || tab === 'deposit', 30_000) })
  const vaultDeposits = useQuery({ ...trpc.vault.deposits.queryOptions(), ...live(tab === 'all' || tab === 'deposit') })
  const vaultWithdrawals = useQuery({ ...trpc.vault.withdrawals.queryOptions(), ...live(tab === 'all' || tab === 'send') })
  // sales for USD on Kraken's book (Nov 1 2026), when Kraken is wired up
  const krakenOrders = useQuery({ ...trpc.kraken.orders.queryOptions(), enabled: kraken.available, ...live(tab === 'all' || tab === 'swap') })
  const krakenWithdrawals = useQuery({ ...trpc.kraken.withdrawals.queryOptions(), enabled: kraken.available, ...live(tab === 'all' || tab === 'send', 30_000) })
  // what is in progress right now, the bottom bar's feed: its flows are the table's Pending rows on every tab
  const progress = useQuery({ ...trpc.progress.list.queryOptions(), ...live(true) })
  const firstSeen = useRef(new Map<string, string>())
  const refresh = useRefreshActivity()

  // today's prices from the holdings, for rows whose record carries no dollar value
  const prices = useMemo(() => pricesFromHoldings(holdings), [holdings])

  // the swap list the open tab reads; the deposit and withdrawal tabs read none
  const swapList = tab === 'all' ? swapsAll : tab === 'swap' ? swapsOwn : tab === 'strategy' ? swapsOrders : null
  const swapItems = useMemo(() => (swapList ? items(swapList) : []), [swapList])
  // the one list the reports export too (lib/activity.ts): same rows, same rules
  const records = useMemo(
    () =>
      buildActivityRows({
        wallet,
        exchange,
        custodyLabel,
        prices,
        deposits: items(deposits),
        transfers: items(transfers),
        swaps: swapItems,
        privateDeposits: privateDeposits.data ?? [],
        privateWithdrawals: privateWithdrawals.data ?? [],
        positions: positions.data ?? [],
        vaultDeposits: vaultDeposits.data ?? [],
        vaultWithdrawals: vaultWithdrawals.data ?? [],
        krakenOrders: krakenOrders.data ?? [],
        krakenWithdrawals: krakenWithdrawals.data ?? [],
      }),
    [wallet, exchange, custodyLabel, prices, deposits.data, transfers.data, swapItems, privateDeposits.data, privateWithdrawals.data, positions.data, vaultDeposits.data, vaultWithdrawals.data, krakenOrders.data, krakenWithdrawals.data],
  )
  // the flows in progress first, as Pending rows; a record's own pending row waits until its flow is done
  const flows = useMemo(() => progress.data?.flows ?? [], [progress.data])
  /*
   * The handover from a flow's Pending row to its record's row, with no gap and no double: a flow whose record row is
   * settled already leaves the row to it; a flow that left the feed lingers until its record is on the table (the
   * feed's push and the lists' polls are not in step: between them an order's row was gone for five to ten seconds,
   * John, Nov 3 2026), and its leaving reads the record's lists again right away rather than on their next poll.
   */
  const lastFlows = useRef(new Map<string, Flow>())
  const [departed, setDeparted] = useState<Map<string, { flow: Flow; at: number }>>(() => new Map())
  useEffect(() => {
    if (!progress.data) return
    const now = new Map(flows.map((flow) => [flow.id, flow]))
    const gone = [...lastFlows.current.values()].filter((flow) => !now.has(flow.id))
    lastFlows.current = now
    if (gone.length === 0 && departed.size === 0) return
    setDeparted((map) => {
      const next = new Map(map)
      for (const flow of gone) next.set(flow.id, { flow, at: Date.now() })
      // a flow back on the feed is live again
      for (const id of now.keys()) next.delete(id)
      return next.size === map.size && gone.length === 0 ? map : next
    })
    const kinds = new Set<FeedKind>()
    for (const flow of gone) for (const kind of RECORD_KINDS[flow.target.kind]) kinds.add(kind)
    if (kinds.size > 0) refresh([...kinds])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows, progress.data, refresh])
  // a lingering flow goes once its record row is here, or after its wait
  useEffect(() => {
    if (departed.size === 0) return
    const prune = () =>
      setDeparted((map) => {
        const next = new Map([...map].filter(([, { flow, at }]) => Date.now() - at < LINGER_MS && !recordOf(flow, records)))
        return next.size === map.size ? map : next
      })
    prune()
    const timer = window.setInterval(prune, 2_000)
    return () => window.clearInterval(timer)
  }, [departed.size, records])
  const shownFlows = useMemo(() => {
    const live = flows.filter((flow) => {
      const record = recordOf(flow, records)
      return !(record && SETTLED.has(record.status))
    })
    return [...live, ...[...departed.values()].map(({ flow }) => flow)]
  }, [flows, records, departed])
  const rows = useMemo(() => [...pendingRows(shownFlows, prices, firstSeen.current), ...records.filter((row) => !coveredByFlow(row, flows))], [shownFlows, flows, prices, records])

  /** the paged lists each tab draws on, and every list it needs before it can show anything */
  const on = (q: Source, enabled: boolean): Source | null => (enabled ? q : null)
  const pagedOf: Record<Tab, Paged[]> = { all: [deposits, transfers, swapsAll], swap: [swapsOwn], strategy: [swapsOrders], deposit: [deposits], send: [transfers] }
  const sourcesOf: Record<Tab, (Source | null)[]> = {
    all: [progress, deposits, transfers, swapsAll, privateDeposits, privateWithdrawals, positions, vaultDeposits, vaultWithdrawals, on(krakenOrders, kraken.available), on(krakenWithdrawals, kraken.available)],
    swap: [progress, swapsOwn, on(krakenOrders, kraken.available)],
    strategy: [progress, swapsOrders],
    deposit: [progress, deposits, privateDeposits, positions, vaultDeposits],
    send: [progress, transfers, privateWithdrawals, vaultWithdrawals, on(krakenWithdrawals, kraken.available)],
  }
  // nothing shows until every list the tab reads is in, so no row slides in after the first paint
  const loading = !kraken.known || sourcesOf[tab].some((q) => q?.isPending)
  // a list the tab reads that could not be loaded says so, above whatever the other lists brought, instead of the
  // table reading as empty (Nov 4 2026); its next poll or the retry asks again
  const failed = !loading && sourcesOf[tab].some((q) => q?.isError === true)
  /*
   * A row is settled once no list could still deliver a newer one. A paged list holds its newest rows; whatever its
   * next page brings is older than its oldest loaded row, so rows older than that line (the latest such line across
   * the tab's lists with more pages) wait for the next page. Older rows then only ever append below the ones on
   * screen, never between them (the Kraken sales that showed first while the exchange sales slid in above them).
   */
  const frontier = pagedOf[tab].reduce((line, q) => {
    if (!q.hasNextPage) return line
    const oldest = q.data?.pages.at(-1)?.items.at(-1)?.createdAt ?? ''
    return oldest > line ? oldest : line
  }, '')
  // a flow in progress is always newer than any page could bring
  const settled = (r: ActivityRow) => r.id.startsWith('flow-') || !frontier || (r.createdAt ?? '') >= frontier

  const needle = query.trim().toLowerCase()
  const onTab = (r: ActivityRow) => (tab === 'all' ? true : tab === 'strategy' ? r.kind === 'swap' && !!r.strategyId : tab === 'swap' ? r.kind === 'swap' && !r.strategyId : r.kind === tab)
  const matching = rows.filter((r) => onTab(r) && settled(r) && (!needle || r.searchText.toLowerCase().includes(needle)))
  const visible = matching.slice(0, shown)
  const more = pagedOf[tab].filter((q) => q.hasNextPage)
  const fetchingMore = pagedOf[tab].some((q) => q.isFetchingNextPage)
  const exhausted = visible.length >= matching.length && more.length === 0

  // a kind's own tab keeps only the columns that apply to it
  const columns = {
    type: tab === 'all' || tab === 'swap' || tab === 'strategy',
    out: tab !== 'deposit',
    in: tab !== 'send',
    counterparty: tab !== 'swap' && tab !== 'strategy',
  }
  const outLabel = tab === 'send' ? 'Amount' : 'Out'
  const inLabel = tab === 'deposit' ? 'Amount' : 'In'

  // a new tab or search starts from the top again
  useEffect(() => {
    setShown(PAGE)
    scroller.current?.scrollTo({ top: 0 })
  }, [tab, needle])

  // the sentinel coming on screen reveals the next rows, and asks the server for the next page once the loaded ones run
  // out; the viewport is the root, so it counts as seen through the card's own scroll (mouse screens) or the page's (touch)
  useEffect(() => {
    const target = sentinel.current
    if (!target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        if (visible.length < matching.length) {
          setShown((n) => n + PAGE)
        } else if (!fetchingMore) {
          more.forEach((q) => void q.fetchNextPage())
        }
      },
      { rootMargin: '0px' },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [visible.length, matching.length, more, fetchingMore])

  return (
    <section className="card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-3">
          <h2 className="card-title">Transactions</h2>
          {/* phones name the open tab beside the title, since its tab is only an icon there; All stays bare (John, Nov 1 2026) */}
          {tab !== 'all' ? (
            <>
              <span className="card-divider sm:hidden" aria-hidden="true" />
              <span className="whitespace-nowrap text-muted sm:hidden">{TABS.find((t) => t.value === tab)?.title}</span>
            </>
          ) : null}
        </div>
        {/* the search box first, the tabs after it (John, Nov 3 2026); phones: the tabs span the card's width, one even
            share each (Nov 1 2026: as words, five of them spilled past its edge) */}
        <div className="grid w-full gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
          <label className="control flex w-full items-center gap-2 px-3 sm:w-64">
            <Search className="size-4 shrink-0 text-muted" />
            <input
              className="w-full bg-transparent py-2 text-base outline-none placeholder:text-muted sm:text-sm"
              placeholder="Search token, address or tx"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <Segmented value={tab} onChange={setTab} options={TABS} className="text-xs" />
        </div>
      </div>
      <div ref={scroller} onScroll={sideways.onScroll} className={`${CARD_SCROLL} overflow-x-auto overscroll-x-contain border-t border-line`}>
        {failed ? (
          <p className="flex items-center gap-3 px-6 py-3 text-sm text-negative">
            Some of these could not be loaded.
            <button type="button" className="btn btn-ghost !px-2 !py-1 text-xs" onClick={() => refresh()}>
              Retry
            </button>
          </p>
        ) : null}
        {loading ? (
          <div className="grid gap-3 p-6"><span className="skeleton h-5 w-2/3" /><span className="skeleton h-5 w-1/2" /></div>
        ) : visible.length === 0 ? (
          // more pages may still hold a match: the spinner row below says so, the words wait for the last page
          more.length > 0 || failed ? null : <p className="px-6 py-6 text-sm text-muted">{needle ? 'Nothing matches.' : EMPTY[tab]}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-surface text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                {/* whichever column comes first is the pinned one */}
                {columns.type ? <th className={`${PIN} ${sideways.edge} px-4 py-2 font-medium sm:px-6`}>Type</th> : null}
                <th className={`py-2 font-medium ${columns.type ? 'px-3' : `${PIN} ${sideways.edge} px-4 sm:px-6`}`}>Token</th>
                <th className="px-3 py-2 font-medium">Platform</th>
                <th className="w-20 px-3 py-2 text-right font-medium">Value</th>
                {columns.out ? <th className="w-28 px-3 py-2 text-right font-medium">{outLabel}</th> : null}
                {columns.in ? <th className="w-28 px-3 py-2 text-right font-medium">{inLabel}</th> : null}
                <th className="w-14 px-3 py-2 font-medium">Age</th>
                {/* set widths (John, Nov 1 and Nov 3 2026): sized by its rows alone, a column started narrow on one tab's dashes and jumped wider on another's figures, so the tabs never lined up; the figure columns and this one keep a floor */}
                {columns.counterparty ? <th className="w-48 px-3 py-2 font-medium">{tab === 'deposit' ? 'From' : tab === 'send' ? 'To' : 'From / To'}</th> : null}
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="w-20 px-3 py-2 text-right font-medium">Fee</th>
                <th className="px-4 py-2 font-medium sm:px-6"><span className="sr-only">Transaction</span></th>
              </tr>
            </thead>
            <tbody>
              {/* every row the one height (John, Nov 3 2026): h-12 is the floor, and nothing in a cell may stand taller
                  than the text line, or that row alone grows past it. Measured in headless Chromium: an inline logo or
                  the 24px link button sitting on the text baseline made a row 49–50px against the 48px floor, so a row
                  without a link (an exchange's bank payout, a failed send) came out shorter than its neighbours. Logos
                  and the link are flex items (no baseline), and the button is pulled out of the flow by 2px each side. */}
              {visible.map((row) => (
                <tr key={row.id} className="h-12 border-t border-line">
                  {columns.type ? (
                    <td className={`${PIN} ${sideways.edge} whitespace-nowrap px-4 py-3 sm:px-6`}>
                      <span className="flex items-center gap-1.5 font-medium">
                        {/* a strategy's order wears the strategy mark, not the swap arrows (John, Nov 1 2026) */}
                        {row.kind === 'deposit' ? (
                          <ArrowDownToLine className="size-3.5 shrink-0 text-muted" />
                        ) : row.kind === 'send' ? (
                          <ArrowUpFromLine className="size-3.5 shrink-0 text-muted" />
                        ) : row.kind === 'swap' && row.strategyId ? (
                          <TrendingUp className="size-3.5 shrink-0 text-muted" />
                        ) : (
                          <ArrowLeftRight className="size-3.5 shrink-0 text-muted" />
                        )}
                        {row.type}
                      </span>
                    </td>
                  ) : null}
                  <td className={`py-3 ${columns.type ? 'px-3' : `${PIN} ${sideways.edge} px-4 sm:px-6`}`}>
                    <span className="flex items-center gap-2"><TokenIcon symbol={row.symbol} imageUrl={row.imageUrl} size="size-5" /> {row.symbol}</span>
                  </td>
                  <td className="px-3 py-3"><span className="flex"><PlatformLogo platform={row.platform} /></span></td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">{row.valueUsd !== undefined ? formatUsdShort(row.valueUsd) : '–'}</td>
                  {columns.out ? <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted">{row.paidLeg ? `${formatTokenAmountShort(row.paidLeg.amount, row.paidLeg.symbol)} ${row.paidLeg.symbol}` : (row.paid ?? '–')}</td> : null}
                  {columns.in ? <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted">{row.receivedLeg ? `${formatTokenAmountShort(row.receivedLeg.amount, row.receivedLeg.symbol)} ${row.receivedLeg.symbol}` : (row.received || '–')}</td> : null}
                  {/* the same size as every other cell (John, Nov 3 2026: "the age text is too small") */}
                  <td className="whitespace-nowrap px-3 py-3 tabular-nums text-muted"><Age at={row.createdAt} /></td>
                  {columns.counterparty ? (
                    <td className="w-48 whitespace-nowrap px-3 py-3 font-mono text-xs text-muted">
                      {row.counterparty ? (
                        <span className="flex items-center gap-1.5 whitespace-nowrap">
                          {tab === 'all' && row.counterpartyDirection ? row.counterpartyDirection : null}
                          {row.counterpartyPlace ? <span className="inline-flex" title={platformLabel(row.counterpartyPlace)}><PlatformMark id={row.counterpartyPlace} size="size-4" /></span> : null}
                          {/* a bank's name (Kraken withdrawals) reads in full; only an address is shortened */}
                          {/\s/.test(row.counterparty) ? row.counterparty : shortAddress(row.counterparty, 6)}
                        </span>
                      ) : '–'}
                    </td>
                  ) : null}
                  <td className={`whitespace-nowrap px-3 py-3 ${row.status === 'errored' || row.status === 'failed' ? 'text-negative' : row.status === 'completed' || row.status === 'confirmed' ? '' : 'text-muted'}`}>
                    {STATUS_LABEL[row.status] ?? row.status}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted" title={feeReceiptText(row.fees) ?? undefined}>
                    {formatFeeShort(row.fees) ?? '–'}
                  </td>
                  <td className="px-4 py-3 sm:px-6">
                    {row.explorerUrl ? (
                      <span className="flex justify-end">
                        <a href={row.explorerUrl} target="_blank" rel="noreferrer" className="btn btn-ghost -my-0.5 !p-1" title="View transaction"><SquareArrowOutUpRight className="size-4" /></a>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* scrolled into view = load more; keeps the card the same height */}
        <div ref={sentinel} className="flex h-10 items-center justify-center text-xs text-muted">
          {fetchingMore ? <Spinner className="size-4" /> : exhausted && matching.length > 0 ? plural(matching.length, 'transaction') : null}
        </div>
      </div>
    </section>
  )
}
